import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Upload, X, FileText, AlertTriangle, CheckCircle } from "lucide-react"
import {
  useGetVendorProposalOrderLinesQuery,
  useGetVendorProposalPurchaseLinesQuery,
  useGetVendorProposalGroupLinesQuery,
  useSubmitVendorProposalMutation,
} from "shared/redux/endpoints/publicVendorProposals"
import NoteBubbleField from "./NoteBubbleField"
import { RULES, fieldError } from "../procurement/fieldRules"

const quoteKey = (l) => (l.offCatalog ? `line-${l.lineId}` : `item-${l.itemId}`)

const MAX_FILE_SIZE_MB = 10
const VENDOR_RULES = RULES.publicVendorProposal

// field -> [label, saved value on the proposal, placeholder]; limits/formats live in VENDOR_RULES
const VENDOR_INFO_FIELDS = [
  { key: "address", label: "Địa chỉ", saved: "proposalAddress", placeholder: "Địa chỉ công ty" },
  { key: "contactName", label: "Người liên hệ", saved: "vendorContactName" },
  { key: "contactPhone", label: "Số điện thoại", saved: "vendorContactPhone" },
  { key: "contactEmail", label: "Email", saved: "vendorContactEmail", email: true },
]

// Warranty months: optional, whole number ≥ 0 (BE 400s on a negative value).
const isValidWarrantyMonths = (v) => v === "" || v == null || (Number.isInteger(Number(v)) && Number(v) >= 0)

function formatVnd(n) {
  if (n === "" || n == null || isNaN(n)) return "—"
  return Number(n).toLocaleString("vi-VN") + " ₫"
}

const formatThousands = (digits) => (digits ? digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "")

function formatFileSize(bytes) {
  if (!bytes) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Public page a supplier opens (no login) to quote or update prices on an
// Order Request's — or, now, a Purchase's — items. The link already carries
// either orderId+supplierId+pin or purchaseId+supplierId+pin in its query
// string (see doc §3.3/3.4, and the PO vendor-proposal API's `url` field) —
// there's no separate PIN-entry gate: viewing the items never needs a pin,
// and the pin travels with the link straight into the submit call. A pin is
// single-use — once a submit succeeds the backend rotates it, so the same
// link can't be reused; the supplier needs a fresh link from the buyer for
// their next update.
export default function VendorProposalPage() {
  const [searchParams] = useSearchParams()
  const orderId = searchParams.get("orderId") || ""
  const purchaseId = searchParams.get("purchaseId") || ""
  // Group Order RFQ links carry groupId instead of orderId/purchaseId.
  const groupId = searchParams.get("groupId") || ""
  const supplierId = searchParams.get("supplierId") || ""
  const pin = searchParams.get("pin") || ""
  const isPurchase = !orderId && !!purchaseId
  const isGroup = !orderId && !purchaseId && !!groupId
  const hasSource = !!(orderId || purchaseId || groupId)

  const orderQuery = useGetVendorProposalOrderLinesQuery(
    { orderRequestId: orderId, supplierId },
    { skip: !orderId }
  )
  const purchaseQuery = useGetVendorProposalPurchaseLinesQuery(
    { purchaseId, supplierId },
    { skip: !purchaseId }
  )
  const groupQuery = useGetVendorProposalGroupLinesQuery(
    { groupOrderId: groupId, supplierId },
    { skip: !isGroup }
  )
  const { data: order, isLoading, isError } = isPurchase ? purchaseQuery : isGroup ? groupQuery : orderQuery
  const [submitVendorProposal, { isLoading: submitting }] = useSubmitVendorProposalMutation()

  // Catalog lines are grouped per item (no lineId); every off-catalog line is
  // its own row with itemId null, so it's keyed — and sent — by its lineId.
  const [quotes, setQuotes] = useState({}) // quoteKey -> { proposedAmount, note, hasAlternative, alternativeNote, warrantyMonths, warrantyNote }
  const [deliveryFee, setDeliveryFee] = useState("")
  const [extraInfo, setExtraInfo] = useState("") // B2 — thông tin bổ sung mức proposal
  const [taxCode, setTaxCode] = useState(null) // null = untouched, show the saved one
  const [vendorInfo, setVendorInfo] = useState({}) // key -> typed value; absent = untouched
  const [answers, setAnswers] = useState({}) // criteriaId -> typed answer; absent = untouched
  const [files, setFiles] = useState([])
  const [fileError, setFileError] = useState("")
  const [submitError, setSubmitError] = useState("")
  const [submitted, setSubmitted] = useState(false)

  const setQuote = (key, patch) =>
    setQuotes((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }))

  const handleAddFiles = (fileList) => {
    setFileError("")
    const picked = Array.from(fileList || [])
    const tooBig = picked.find((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024)
    if (tooBig) {
      setFileError(`${tooBig.name}: file quá ${MAX_FILE_SIZE_MB}MB.`)
      return
    }
    setFiles((prev) => [...prev, ...picked])
  }
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx))

  const lines = order?.lines || []
  // OR/PO requirement set; group lines never carry it.
  const requirements = isGroup ? [] : [...(order?.requirements || [])].sort((a, b) => a.orderNo - b.orderNo)
  const answerValue = (r) => answers[r.criteriaId] ?? r.answer ?? ""
  const hasAtLeastOnePrice = lines.some((l) => Number(quotes[quoteKey(l)]?.proposedAmount) > 0)
  // A quoted amount covers the line's whole quantity as one lump sum, not a
  // per-unit price — so the total is a plain sum, never quantity-weighted.
  const estimatedTotal = lines.reduce(
    (sum, l) => sum + (Number(quotes[quoteKey(l)]?.proposedAmount) || 0),
    0
  )
  const deliveryFeeValid = deliveryFee === "" || Number(deliveryFee) >= 0
  const warrantyValid = lines.every((l) => isValidWarrantyMonths(quotes[quoteKey(l)]?.warrantyMonths))
  const taxCodeValue = taxCode ?? order?.proposalTaxCode ?? ""
  const taxCodeError = fieldError(taxCodeValue, VENDOR_RULES.taxCode)
  const taxCodeValid = !taxCodeError
  const infoValue = (f) => vendorInfo[f.key] ?? order?.[f.saved] ?? ""
  const infoError = (f) => fieldError(infoValue(f), VENDOR_RULES[f.key])
  const infoValid = VENDOR_INFO_FIELDS.every((f) => !infoError(f))
  const canSubmit =
    hasSource &&
    !!supplierId &&
    hasAtLeastOnePrice &&
    deliveryFeeValid &&
    warrantyValid &&
    taxCodeValid &&
    infoValid &&
    !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitError("")
    try {
      await submitVendorProposal({
        orderRequestId: orderId || undefined,
        purchaseId: purchaseId || undefined,
        groupOrderId: isGroup ? groupId : undefined,
        supplierId,
        pin,
        // proposedAmount is the lump-sum quote for the line's whole quantity,
        // sent exactly as entered — nothing multiplies it by quantity.
        proposals: lines
          .filter((l) => Number(quotes[quoteKey(l)]?.proposedAmount) > 0)
          .map((l) => ({
            ...(l.offCatalog ? { orderRequestLineId: l.lineId } : { itemId: l.itemId }),
            proposedAmount: Number(quotes[quoteKey(l)].proposedAmount),
            note: quotes[quoteKey(l)]?.note || undefined,
            // B1 — alternative goods offered for this line.
            hasAlternative: !!quotes[quoteKey(l)]?.hasAlternative,
            alternativeNote: quotes[quoteKey(l)]?.hasAlternative
              ? quotes[quoteKey(l)]?.alternativeNote || undefined
              : undefined,
            warrantyMonths:
              quotes[quoteKey(l)]?.warrantyMonths === "" || quotes[quoteKey(l)]?.warrantyMonths == null
                ? undefined
                : Number(quotes[quoteKey(l)].warrantyMonths),
            warrantyNote: quotes[quoteKey(l)]?.warrantyNote?.trim() || undefined,
          })),
        // B2 — free-text extra note for the whole proposal (BE: vendor_extra_note).
        extraNote: extraInfo.trim() || undefined,
        taxCode: taxCodeValue.trim() || undefined,
        vendorInfo: Object.fromEntries(
          [
            ...VENDOR_INFO_FIELDS.map((f) => [f.key, infoValue(f).trim()]),
            ["alternative", (vendorInfo.alternative ?? order?.alternative ?? "").trim()],
            ["extraInfo", (vendorInfo.extraInfo ?? order?.extraInfo ?? "").trim()],
          ]
        ),
        deliveryFee: deliveryFee === "" ? undefined : Number(deliveryFee),
        // Only touched answers; "" clears a saved one, an omitted id keeps it.
        requirementAnswers: Object.fromEntries(
          requirements.filter((r) => r.criteriaId in answers).map((r) => [r.criteriaId, answers[r.criteriaId].trim()])
        ),
        files,
      }).unwrap()
      setSubmitted(true)
    } catch (e) {
      if (e?.status === 410) {
        // PIN is correct but the deadline (expiredAt) has already passed.
        setSubmitError(
          "Đã quá hạn nhận báo giá cho liên kết này. Vui lòng liên hệ bên mua để nhận liên kết báo giá mới."
        )
      } else if (e?.status === 403) {
        setSubmitError(
          "Liên kết này không hợp lệ hoặc đã được sử dụng. Vui lòng liên hệ bên mua để nhận liên kết báo giá mới."
        )
      } else {
        setSubmitError(
          e?.data?.data?.message || "Không thể gửi báo giá. Vui lòng thử lại."
        )
      }
    }
  }

  if (!hasSource || !supplierId) {
    return (
      <ScreenMessage
        icon={<AlertTriangle size={40} color="#dc2626" />}
        title="Liên kết không hợp lệ"
        desc="Đường liên kết này thiếu thông tin. Vui lòng liên hệ bên mua để nhận lại liên kết."
      />
    )
  }

  if (isLoading) {
    return <ScreenMessage title="Đang tải…" />
  }

  if (isError || !order) {
    return (
      <ScreenMessage
        icon={<AlertTriangle size={40} color="#dc2626" />}
        title="Không tìm thấy đơn hàng"
        desc="Đơn hàng này không tồn tại hoặc đã bị gỡ bỏ."
      />
    )
  }

  if (submitted) {
    return (
      <ScreenMessage
        icon={<CheckCircle size={40} color="#16a34a" />}
        title="Đã gửi báo giá thành công"
        desc="Cảm ơn bạn đã gửi báo giá. Lưu ý: liên kết này đã được sử dụng và không còn hiệu lực — nếu cần chỉnh sửa lại, vui lòng liên hệ bên mua để nhận liên kết mới."
      />
    )
  }

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 16px", fontFamily: "inherit" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#101828" }}>
          {order.rfqTitle || "Yêu cầu báo giá"}
        </div>
        <div style={{ fontSize: 13.5, color: "#5a6478", marginTop: 4 }}>
          {isPurchase ? "Mã PO" : isGroup ? "Mã nhóm đơn" : "Mã đơn"}:{" "}
          <strong>{order.poCode || order.groupCode || order.orderCode}</strong>
          {order.title ? ` · ${order.title}` : ""}
          {order.region ? ` · ${order.region}` : ""}
        </div>
      </div>

      {(order.termsText ||
        requirements.length > 0 ||
        order.requiredDocumentsText ||
        order.deliveryLocation ||
        order.deliveryDate ||
        order.noteForVendor ||
        order.contactName ||
        order.contactEmail ||
        order.contactPhone) && (
        <div
          style={{
            border: "1px solid #e2e6ed",
            borderRadius: 10,
            padding: "16px 18px",
            marginBottom: 20,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            fontSize: 13.5,
          }}
        >
          {requirements.length > 0 ? (
            <div>
              <div style={{ fontWeight: 600, color: "#101828", marginBottom: 4 }}>Yêu cầu / điều khoản</div>
              <div style={{ color: "#8a93a6", fontSize: 12.5, marginBottom: 8 }}>
                Trả lời từng yêu cầu (tuỳ chọn).
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {requirements.map((r) => (
                  <label key={r.criteriaId} style={{ display: "block", color: "#344054" }}>
                    {r.name}
                    <textarea
                      rows={1}
                      placeholder="Câu trả lời của bạn"
                      value={answerValue(r)}
                      onChange={(e) => setAnswers((prev) => ({ ...prev, [r.criteriaId]: e.target.value }))}
                      style={answerStyle}
                    />
                  </label>
                ))}
              </div>
            </div>
          ) : (
            order.termsText && (
              <div>
                <div style={{ fontWeight: 600, color: "#101828", marginBottom: 4 }}>Yêu cầu / điều khoản</div>
                <div style={{ color: "#344054", whiteSpace: "pre-line" }}>{order.termsText}</div>
              </div>
            )
          )}
          {order.requiredDocumentsText && (
            <div>
              <div style={{ fontWeight: 600, color: "#101828", marginBottom: 4 }}>
                Tài liệu yêu cầu đính kèm <span style={{ fontWeight: 400, color: "#8a93a6" }}>(* = bắt buộc)</span>
              </div>
              <div style={{ color: "#344054" }}>{order.requiredDocumentsText}</div>
            </div>
          )}
          {(order.deliveryLocation || order.deliveryDate) && (
            <div>
              <div style={{ fontWeight: 600, color: "#101828", marginBottom: 4 }}>Giao hàng</div>
              <div style={{ color: "#344054" }}>
                {order.deliveryLocation}
                {order.deliveryLocation && order.deliveryDate ? " · " : ""}
                {order.deliveryDate ? `Dự kiến ${order.deliveryDate}` : ""}
              </div>
            </div>
          )}
          {order.noteForVendor && (
            <div>
              <div style={{ fontWeight: 600, color: "#101828", marginBottom: 4 }}>Ghi chú từ bên mua</div>
              <div style={{ color: "#344054", whiteSpace: "pre-line" }}>{order.noteForVendor}</div>
            </div>
          )}
          {(order.contactName || order.contactEmail || order.contactPhone) && (
            <div>
              <div style={{ fontWeight: 600, color: "#101828", marginBottom: 4 }}>Người liên hệ</div>
              <div style={{ color: "#344054" }}>
                {[order.contactName, order.contactEmail, order.contactPhone].filter(Boolean).join(" · ")}
              </div>
            </div>
          )}
        </div>
      )}

      {submitError && (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            padding: "10px 14px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 8,
            color: "#b42318",
            fontSize: 13.5,
            marginBottom: 16,
          }}
        >
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{submitError}</span>
        </div>
      )}

      <div style={{ border: "1px solid #e2e6ed", borderRadius: 10, overflowX: "auto", marginBottom: 20 }}>
        {/* Fixed layout so toggling the alternative field doesn't reflow column widths. */}
        <table style={{ width: "100%", minWidth: 1080, tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13.5 }}>
          <colgroup>
            <col />
            <col style={{ width: 64 }} />
            <col style={{ width: 84 }} />
            <col style={{ width: 170 }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 190 }} />
            <col style={{ width: 190 }} />
          </colgroup>
          <thead>
            <tr style={{ background: "#f8f9fb", textAlign: "left" }}>
              <th style={th}>Hạng mục</th>
              <th style={th}>SL</th>
              <th style={th}>Đơn vị</th>
              <th style={th}>Báo giá cho cả SL (VND)</th>
              <th style={th}>Ghi chú</th>
              <th style={th}>Bảo hành</th>
              <th style={th}>Hàng thay thế?</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={quoteKey(l)} style={{ borderTop: "1px solid #eef0f4" }}>
                <td style={td}>
                  <div style={{ fontWeight: 600, color: "#101828" }}>{l.itemName}</div>
                  {l.itemCode && <div style={{ color: "#8a93a6", fontSize: 12 }}>{l.itemCode}</div>}
                  {l.description && <div style={{ color: "#5a6478", fontSize: 12, marginTop: 2 }}>{l.description}</div>}
                  {l.note && <div style={{ color: "#5a6478", fontSize: 12, marginTop: 2 }}>{l.note}</div>}
                </td>
                <td style={td}>{l.quantity}</td>
                <td style={td}>{l.uom}</td>
                <td style={td}>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    value={formatThousands(quotes[quoteKey(l)]?.proposedAmount)}
                    onChange={(e) => setQuote(quoteKey(l), { proposedAmount: e.target.value.replace(/\D/g, "") })}
                    style={inputStyle}
                  />
                </td>
                <td style={td}>
                  <NoteBubbleField
                    placeholder="Model / ghi chú"
                    value={quotes[quoteKey(l)]?.note ?? ""}
                    onChange={(e) => setQuote(quoteKey(l), { note: e.target.value })}
                  />
                </td>
                <td style={td}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      placeholder="0"
                      aria-label="Số tháng bảo hành"
                      value={quotes[quoteKey(l)]?.warrantyMonths ?? ""}
                      onChange={(e) => setQuote(quoteKey(l), { warrantyMonths: e.target.value })}
                      style={{
                        ...inputStyle,
                        width: 72,
                        borderColor: isValidWarrantyMonths(quotes[quoteKey(l)]?.warrantyMonths) ? "#e2e6ed" : "#dc2626",
                      }}
                    />
                    <span style={{ fontSize: 12.5, color: "#5a6478" }}>tháng</span>
                  </div>
                  {!isValidWarrantyMonths(quotes[quoteKey(l)]?.warrantyMonths) && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>Số tháng là số nguyên ≥ 0.</div>
                  )}
                  <NoteBubbleField
                    placeholder="Điều kiện bảo hành"
                    value={quotes[quoteKey(l)]?.warrantyNote ?? ""}
                    onChange={(e) => setQuote(quoteKey(l), { warrantyNote: e.target.value })}
                    style={{ marginTop: 6 }}
                  />
                </td>
                <td style={td}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={!!quotes[quoteKey(l)]?.hasAlternative}
                      onChange={(e) => setQuote(quoteKey(l), { hasAlternative: e.target.checked })}
                    />
                    Có
                  </label>
                  {quotes[quoteKey(l)]?.hasAlternative && (
                    <NoteBubbleField
                      placeholder="Mô tả hàng thay thế"
                      value={quotes[quoteKey(l)]?.alternativeNote ?? ""}
                      onChange={(e) => setQuote(quoteKey(l), { alternativeNote: e.target.value })}
                      style={{ marginTop: 6 }}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          fontSize: 14,
          marginBottom: 20,
        }}
      >
        <span style={{ color: "#5a6478" }}>Tổng báo giá:</span>
        <strong style={{ color: "#101828" }}>{formatVnd(estimatedTotal)}</strong>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#101828", marginBottom: 8 }}>
          Mã số thuế (tuỳ chọn)
        </div>
        <input
          type="text"
          maxLength={VENDOR_RULES.taxCode.max}
          placeholder="Mã số thuế xuất hoá đơn"
          value={taxCodeValue}
          onChange={(e) => setTaxCode(e.target.value)}
          style={{ ...inputStyle, maxWidth: 240, borderColor: taxCodeValid ? "#e2e6ed" : "#dc2626" }}
        />
        <div style={{ color: taxCodeValid ? "#8a93a6" : "#dc2626", fontSize: 12, marginTop: 4 }}>
          {taxCodeError || `Tối đa ${VENDOR_RULES.taxCode.max} ký tự.`}
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#101828", marginBottom: 8 }}>
          Thông tin nhà cung cấp (tuỳ chọn)
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          {VENDOR_INFO_FIELDS.map((f) => {
            const v = infoValue(f)
            const error = infoError(f)
            return (
              <label key={f.key} style={{ fontSize: 12.5, color: "#5a6478" }}>
                {f.label}
                <input
                  type={f.email ? "email" : "text"}
                  maxLength={VENDOR_RULES[f.key].max}
                  placeholder={f.placeholder}
                  value={v}
                  onChange={(e) => setVendorInfo((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  style={{ ...inputStyle, marginTop: 4, borderColor: error ? "#dc2626" : "#e2e6ed" }}
                />
                {error && <span style={{ color: "#dc2626" }}>{error}</span>}
              </label>
            )
          })}
        </div>
        <label style={{ display: "block", fontSize: 12.5, color: "#5a6478", marginTop: 12 }}>
          Phương án thay thế (cả báo giá)
          <textarea
            rows={2}
            placeholder="VD: Có thể thay bằng model X nếu hết hàng"
            value={vendorInfo.alternative ?? order?.alternative ?? ""}
            onChange={(e) => setVendorInfo((prev) => ({ ...prev, alternative: e.target.value }))}
            style={{ ...inputStyle, marginTop: 4, resize: "vertical" }}
          />
        </label>
        <label style={{ display: "block", fontSize: 12.5, color: "#5a6478", marginTop: 12 }}>
          Thông tin khác (điều khoản thanh toán…)
          <textarea
            rows={2}
            placeholder="VD: Thanh toán 30 ngày"
            value={vendorInfo.extraInfo ?? order?.extraInfo ?? ""}
            onChange={(e) => setVendorInfo((prev) => ({ ...prev, extraInfo: e.target.value }))}
            style={{ ...inputStyle, marginTop: 4, resize: "vertical" }}
          />
        </label>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#101828", marginBottom: 8 }}>
          Phí giao hàng (VND, tuỳ chọn)
        </div>
        <input
          type="number"
          min={0}
          placeholder="0"
          value={deliveryFee}
          onChange={(e) => setDeliveryFee(e.target.value)}
          style={{ ...inputStyle, maxWidth: 240 }}
        />
        <div style={{ color: deliveryFeeValid ? "#8a93a6" : "#dc2626", fontSize: 12, marginTop: 4 }}>
          {deliveryFeeValid ? "Tính riêng, không cộng vào tổng báo giá ở trên." : "Phí giao hàng không được âm."}
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#101828", marginBottom: 8 }}>
          Thông tin bổ sung (tuỳ chọn)
        </div>
        <textarea
          rows={3}
          placeholder="Điều khoản giao hàng, bảo hành, chiết khấu, hoặc bất kỳ ghi chú nào gửi bên mua…"
          value={extraInfo}
          onChange={(e) => setExtraInfo(e.target.value)}
          style={{ ...inputStyle, resize: "vertical" }}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#101828", marginBottom: 8 }}>
          Tài liệu đính kèm (tuỳ chọn)
        </div>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
            border: "1px dashed #c9cfdb",
            borderRadius: 8,
            cursor: "pointer",
            fontSize: 13,
            color: "#5a6478",
            width: "fit-content",
          }}
        >
          <Upload size={16} />
          Chọn file để đính kèm
          <input
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => handleAddFiles(e.target.files)}
          />
        </label>
        {fileError && <div style={{ color: "#dc2626", fontSize: 12.5, marginTop: 6 }}>{fileError}</div>}
        {files.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
            {files.map((f, i) => (
              <div
                key={`${f.name}-${i}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12.5,
                  color: "#101828",
                  background: "#f8f9fb",
                  borderRadius: 6,
                  padding: "6px 10px",
                }}
              >
                <FileText size={14} />
                <span style={{ flex: 1 }}>{f.name}</span>
                <span style={{ color: "#8a93a6" }}>{formatFileSize(f.size)}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#8a93a6", display: "flex" }}
                  aria-label={`Remove ${f.name}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        style={{
          width: "100%",
          padding: "12px 16px",
          borderRadius: 8,
          border: "none",
          background: canSubmit ? "#3b45c4" : "#c9cfdb",
          color: "#fff",
          fontSize: 14.5,
          fontWeight: 700,
          cursor: canSubmit ? "pointer" : "not-allowed",
        }}
      >
        {submitting ? "Đang gửi…" : "Gửi báo giá"}
      </button>
      {!hasAtLeastOnePrice && (
        <div style={{ fontSize: 12.5, color: "#8a93a6", marginTop: 8, textAlign: "center" }}>
          Nhập báo giá cho ít nhất 1 hạng mục để gửi báo giá.
        </div>
      )}
    </div>
  )
}

function ScreenMessage({ icon, title, desc }) {
  return (
    <div
      style={{
        maxWidth: 480,
        margin: "80px auto",
        padding: 24,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
      }}
    >
      {icon}
      <div style={{ fontSize: 18, fontWeight: 700, color: "#101828" }}>{title}</div>
      {desc && <div style={{ fontSize: 13.5, color: "#5a6478" }}>{desc}</div>}
    </div>
  )
}

const th = { padding: "10px 14px", fontWeight: 600, color: "#5a6478", fontSize: 12.5 }
const td = { padding: "10px 14px", verticalAlign: "top" }
const inputStyle = {
  width: "100%",
  padding: "6px 8px",
  border: "1px solid #e2e6ed",
  borderRadius: 6,
  fontSize: 13,
  boxSizing: "border-box",
}

// Textareas don't inherit the page font; one line tall, grows with the answer
// (`fieldSizing` where supported, otherwise the user drags the handle).
const answerStyle = {
  ...inputStyle,
  display: "block",
  marginTop: 4,
  fontFamily: "inherit",
  lineHeight: 1.5,
  resize: "vertical",
  fieldSizing: "content",
  minHeight: 32,
}
